<template>
    <Teleport to="body">
        <Transition :name="animate ? 'ds-modal-dialog-animation' : undefined" :duration="animate ? 250 : undefined">
            <div
                v-if="modelValueInternal"
                class="ds-modal-dialog"
                :class="[
                    cssClass,
                    modalPosition,
                    {
                        'ds-modal-dialog--content-scroll': contentScrollable,
                        'ds-modal-dialog--sticky-bottom': stickyBottom,
                    },
                    `ds-modal-dialog--${theme}`,
                ]"
            >
                <div
                    class="ds-modal-dialog__background"
                    :class="backgroundClass"
                    @click="closeOnClickOutside ? closeDialogInternal() : null"
                ></div>
                <div
                    class="ds-modal-dialog__body"
                    role="dialog"
                    :class="bodyClass"
                    :data-test="dataTest"
                    aria-modal="true"
                    :aria-labelledby="dialogLabelId"
                    ref="dialogBodyRef"
                >
                    <DsButton
                        type="primary"
                        :theme="theme"
                        :size="closeButtonSize"
                        class="ds-modal-dialog__close"
                        aria-label="Zavřít"
                        icon-left="cross"
                        @click="closeDialogInternal()"
                        data-test="ds-modal-close"
                        v-if="hasDialogCloseBtn"
                    />
                    <div class="ds-modal-dialog__container">
                        <slot name="top"></slot>
                        <DsDivider
                            v-if="topDividerVisible && slots.top && (slots.content || slots.bottom)"
                            class="ds-modal-dialog__top-divider"
                            :theme="theme"
                        />
                        <div class="ds-modal-dialog__content" v-if="!!slots.content" ref="contentRef">
                            <slot name="content"></slot>
                        </div>
                        <DsDivider
                            v-if="bottomDividerVisible && (slots.top || slots.content) && slots.bottom"
                            class="ds-modal-dialog__bottom-divider"
                            :theme="theme"
                        />
                        <slot v-if="!stickyBottom" name="bottom"></slot>

                        <div class="ds-modal-dialog__sticky-bottom" v-else-if="slots.bottom">
                            <slot name="bottom"></slot>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script lang="ts" setup>
import { provide, useSlots, computed, watch, onBeforeUnmount, ref, nextTick } from "vue";
import { useFocusTrap } from "@allwyn/utils/composables/use-focus-trap";
import { useStackedModals } from "@allwyn/utils/composables/use-stacked-modals";
import { isModalVisibleInjectionKey } from "./ds-modal-dialog-injection-key";
import { DsModalDialogExposeType, ModalPosition } from "./ds-modal-dialog-models";
import DsButton from "../button/ds-button.vue";
import DsDivider from "../divider/ds-divider.vue";
import { Size } from "../types/size";
import { Theme } from "../types/theme";

const dialogBodyRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);

const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(dialogBodyRef, {
    initialFocus: false,
    allowOutsideClick: true,
});

const slots = useSlots();
const props = withDefaults(
    defineProps<{
        /**
         * Dialog aria-labelledby id
         */
        dialogLabelId?: string;
        /**
         * Handles showing / hiding modal
         */
        modelValue: boolean;
        /**
         * Modal position
         */
        modalPosition?: ModalPosition;
        /**
         * Enable / disable top divider
         */
        topDividerVisible?: boolean;
        /**
         * Enable / disable bottom divider
         */
        bottomDividerVisible?: boolean;
        /**
         * Set theme
         */
        theme?: Theme;
        /**
         * Make modal content scrollable instead of the whole modal
         */
        contentScrollable?: boolean;
        /**
         * Make the bottom section of the modal sticky
         */
        stickyBottom?: boolean;
        /**
         * Data test attr
         */
        dataTest?: string;
        /**
         * Show / hide close button of dialog
         */
        hasDialogCloseBtn?: boolean;
        /**
         * Enable / disable closing behavior when clicked on background
         */
        closeOnClickOutside?: boolean;
        /**
         * Animate modal opening / closing
         */
        animate?: boolean;
        /**
         * Prevent modal from closing
         */
        preventClose?: boolean;
        /**
         * Custom class for your css
         */
        cssClass?: string | string[];
        /**
         * Size of the close button
         */
        closeButtonSize?: Size;
        /**
         * Enable trap focus
         */
        trapFocus?: boolean;
    }>(),
    {
        modalPosition: "top",
        topDividerVisible: true,
        bottomDividerVisible: true,
        theme: "light",
        contentScrollable: false,
        hasDialogCloseBtn: true,
        closeOnClickOutside: true,
        animate: false,
        preventClose: false,
        cssClass: "",
        closeButtonSize: "default",
        trapFocus: false,
    }
);

const emit = defineEmits(["update:modelValue", "modal-closed"]);

// dont use defineModal macro. If you have v-if on the parent, it doesnt remove from StackedModals.
// DefineModal can use later maybe, its too much new feature.
const modelValueInternal = computed({
    get() {
        return props.modelValue;
    },
    set(newValue) {
        emit("update:modelValue", newValue);
    },
});

/**
 * Handles showing / hiding modal
 */
provide(isModalVisibleInjectionKey, modelValueInternal);

const closeDialogInternal = () => {
    if (props.preventClose) return;

    modelValueInternal.value = false;
    emit("modal-closed");
};

const keyDownEventHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        closeDialogInternal();
    }
};

const { addToStackedModals, removeFromStackedModals } = useStackedModals();
const dialogIsOpening = () => {
    window.addEventListener("keydown", keyDownEventHandler);

    if (props.trapFocus) {
        nextTick(() => {
            activateFocusTrap();
        });
    }

    addToStackedModals();
};

const dialogIsClosing = () => {
    window.removeEventListener("keydown", keyDownEventHandler);

    if (props.trapFocus) {
        deactivateFocusTrap();
    }

    removeFromStackedModals();
};

watch(
    modelValueInternal,
    (newModelValue, oldModelValue) => {
        if (newModelValue && !oldModelValue) {
            dialogIsOpening();
        } else if (!newModelValue && oldModelValue) {
            dialogIsClosing();
        }
    },
    {
        immediate: true,
    }
);

onBeforeUnmount(() => {
    if (modelValueInternal.value) {
        dialogIsClosing();
    }
});

const backgroundClass = computed(() => {
    if (Array.isArray(props.cssClass)) {
        return props.cssClass.filter(Boolean).map((cssClass) => `${cssClass}__background`);
    }
    return props.cssClass ? `${props.cssClass}__background` : undefined;
});

const bodyClass = computed(() => {
    if (Array.isArray(props.cssClass)) {
        return props.cssClass.filter(Boolean).map((cssClass) => `${cssClass}__body`);
    }
    return props.cssClass ? `${props.cssClass}__body` : undefined;
});

const scrollContent = (topPosition = 0, smoth = false) => {
    if (!props.contentScrollable || !contentRef.value) {
        return;
    }

    contentRef.value.scroll({ top: topPosition, behavior: smoth ? "smooth" : "instant" });
};

defineExpose<DsModalDialogExposeType>({
    dialogBodyRef,
    scrollContent,
});
</script>
